import { ArrowLeft } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { cn } from '@Utils';

import { Badge } from '@Components';
import { Button } from '@Components/ui-v2/button';
import SegmentedControl from '@Components/ui-v2/segmented-control';
import { useHasPermission } from '@CustomHooks';
import { FieldServiceDetail as FieldServiceDetailModel, Permission } from '@Models';
import AvailabilitySection from './sections/AvailabilitySection';
import CoverageSection from './sections/CoverageSection';
import IdentitySection from './sections/IdentitySection';
import TaskTypesSection from './sections/TaskTypesSection';
import WorkersSection from './sections/WorkersSection';
import { getContractStatusVariant, isKnownContractStatus, PAGE_HEADER_BAND_CLASS_NAME } from './constants';

interface FieldServiceDetailProps {
  detail: FieldServiceDetailModel;
  color: string;
}

type DetailMode = 'viewer' | 'edit';

const FieldServiceDetail = ({ detail, color }: FieldServiceDetailProps) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const canManage = useHasPermission(Permission.ManageFieldServices);

  const statusVariant = getContractStatusVariant(detail.contract.status);
  const statusLabel = isKnownContractStatus(detail.contract.status)
    ? t(`FieldService.ContractStatus.${detail.contract.status}`)
    : detail.contract.status;

  return (
    <div className="flex h-full w-full flex-col">
      <div className={cn('flex flex-wrap items-center gap-3 bg-white', PAGE_HEADER_BAND_CLASS_NAME)}>
        <div className="flex min-w-0 flex-auto items-center gap-3">
          <Button
            variant="outline"
            size="sm"
            onClick={() => navigate('/field-services')}
            aria-label={t('FieldService.BackToList')}
            className="h-8 flex-shrink-0 border-gray-200 bg-white text-xs font-semibold text-gray-700 hover:bg-gray-50">
            <ArrowLeft size={16} />
            <span className="hidden sm:inline">{t('FieldService.BackToList')}</span>
          </Button>
          <IdentitySection fieldService={detail.field_service} color={color} />
        </div>
        <div className="ml-auto flex flex-shrink-0 items-center gap-3">
          <Badge
            variant={statusVariant}
            label={statusLabel}
            showDot
            size="sm"
            className={cn('rounded-full', statusVariant === 'gray' && 'text-gray-500')}
          />
          {canManage && (
            <SegmentedControl<DetailMode>
              value="viewer"
              disabled
              ariaLabel={t('FieldService.ModeAriaLabel')}
              className="gap-0.5 rounded-[9px] bg-gray-50 p-[3px]"
              optionClassName="h-7 rounded-[7px] px-4 text-xs font-semibold transition-colors"
              activeOptionClassName="bg-white text-gray-900 shadow-sm ring-1 ring-gray-200"
              options={[
                { value: 'viewer', label: t('FieldService.ModeViewer') },
                { value: 'edit', label: t('FieldService.ModeEdit') },
              ]}
            />
          )}
        </div>
      </div>

      <div className="mx-auto flex w-full max-w-[800px] flex-col gap-4 px-4 py-6 sm:px-7">
        <CoverageSection
          retailer={detail.contract.retailer}
          stores={detail.stores}
          totalStores={detail.retailer_store_count}
        />
        <AvailabilitySection contract={detail.contract} />
        <TaskTypesSection contract={detail.contract} />
        {detail.contract.workers_enabled && <WorkersSection workers={detail.workers} />}
      </div>
    </div>
  );
};

export default FieldServiceDetail;
